import React, { useState, useEffect } from 'react';
import { Poll, CreatePollRequest, CreatePollAlternativeRequest } from '../../Core/Types/poll';
import { useGetOnePoll, useUpdatePoll } from '../../Core/Hooks/usePollHooks';
import { useForm } from '../../Core/Hooks/useForm';
import { requiredValidator } from '../../Core/Hooks/useForm';
import BizInput from '../../Core/Components/Mini/BizInput';
import { BizCheckBox } from '../../Core/Components/Mini/BizCheckBox';
import { BizButton } from '../../Core/Components/Mini/BizButton';
import BizIconButton from '../../Core/Components/Mini/BizIconButton';
import './PollEdit.scss';

interface PollEditProps {
	pollId?: number;
	onSave?: (poll: CreatePollRequest) => void;
	onClose: () => void;
	statusState?: string;
}

const PollEdit = ({ pollId, onSave, onClose }: PollEditProps) => {
	const { poll: existingPoll, statusState: pollStatusState } = pollId
		? useGetOnePoll(pollId)
		: { poll: null, statusState: 'SUCCESS' };
	const { updatePoll } = useUpdatePoll();

	const [question, setQuestion] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [alternatives, setAlternatives] = useState<CreatePollAlternativeRequest[]>([
		{ Alternative: '', Order: 1, IsCorrect: false },
		{ Alternative: '', Order: 2, IsCorrect: false },
	]);

	const { formState } = useForm({});

	useEffect(() => {
		if (existingPoll) {
			setQuestion(existingPoll.Question);
			setIsActive(existingPoll.IsActive);
			setAlternatives(
				existingPoll.Alternatives.map((alt) => ({
					Alternative: alt.Alternative,
					Order: alt.Order,
					IsCorrect: alt.IsCorrect || false,
				}))
			);
		}
	}, [existingPoll]);

	const handleAddAlternative = () => {
		const newOrder = Math.max(...alternatives.map((alt) => alt.Order), 0) + 1;
		setAlternatives([...alternatives, { Alternative: '', Order: newOrder, IsCorrect: false }]);
	};

	const handleRemoveAlternative = (index: number) => {
		if (alternatives.length > 2) {
			setAlternatives(alternatives.filter((_, i) => i !== index));
		}
	};

	const handleAlternativeChange = (index: number, field: keyof CreatePollAlternativeRequest, value: string | boolean) => {
		const updated = alternatives.map((alt, i) => (i === index ? { ...alt, [field]: value } : alt));
		setAlternatives(updated);
	};

	const handleSave = async () => {
		if (!formState.isFormValid) {
			formState.setShowValidation(true);
			return;
		}

		const validAlternatives = alternatives.filter((alt) => alt.Alternative.trim() !== '');

		if (validAlternatives.length < 2) {
			alert('Please provide at least 2 alternatives');
			return;
		}

		const pollData: CreatePollRequest = {
			Question: question,
			IsActive: isActive,
			Alternatives: validAlternatives,
		};

		if (existingPoll && !onSave) {
			// Update existing poll
			const updatedPoll: Poll = {
				...existingPoll,
				...pollData,
				Alternatives: validAlternatives.map((alt, index) => ({
					Id: existingPoll.Alternatives[index]?.Id || 0,
					PollId: existingPoll.Id,
					Alternative: alt.Alternative,
					IsDeleted: false,
					Order: alt.Order,
					IsCorrect: alt.IsCorrect,
					VoteCount: existingPoll.Alternatives[index]?.VoteCount || 0,
					VotePercentage: existingPoll.Alternatives[index]?.VotePercentage || 0,
				})),
			};
			await updatePoll(updatedPoll);
			onClose();
		} else if (onSave) {
			// Create new poll
			onSave(pollData);
		}
	};

	return (
		<div className='biz-poll-edit' onClick={(e) => e.stopPropagation()}>
			<div>
				<div className={`${formState.showValidation ? 'show-validation' : ''}`}>
					{pollStatusState === 'LOADING' && <div>Loading poll...</div>}
					{pollStatusState === 'ERROR' && <div>Error loading poll</div>}
					{pollStatusState === 'SUCCESS' && (
						<>
							<div>
								<div className='form-group'>
									<span className='control-label'>Question</span>
									<BizInput
										className='form-control'
										name='Question'
										type='text'
										value={question}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuestion(e.target.value)}
										formConfig={{ state: formState, validators: [requiredValidator] }}
										legacy
									/>
								</div>
								<div className='margin-top'>
									<BizCheckBox
										value={isActive}
										onChange={(checked) => setIsActive(checked)}
										label='Active'
										formConfig={{ state: formState }}
									/>
								</div>
							</div>

							<div className='margin-top'>
								<div className='panel-header'>
									<span className='control-label'>Alternatives</span>
									<BizIconButton icon='ADD' onClick={handleAddAlternative} legacy title='Add alternative' />
								</div>

								{alternatives.map((alternative, index) => (
									<div key={index} className='alternative-row margin-top'>
										<div className='alternative-input'>
											<BizInput
												className='form-control'
												name={`Alternative${index}`}
												type='text'
												value={alternative.Alternative}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													handleAlternativeChange(index, 'Alternative', e.target.value)
												}
												formConfig={{ state: formState, validators: [requiredValidator] }}
												legacy
											/>
										</div>
										<div className='alternative-actions'>
											{alternatives.length > 2 && (
												<BizIconButton
													icon='REMOVE'
													onClick={() => handleRemoveAlternative(index)}
													legacy
													title='Remove alternative'
												/>
											)}
										</div>
									</div>
								))}
							</div>
						</>
					)}
				</div>
				<div className='actions dialog-btn-actions'>
					<BizButton
						text='Save'
						onClick={handleSave}
						isLead={formState.isDirty}
						legacy={false}
					/>
					<BizButton text='Cancel' onClick={onClose} legacy={false} />
				</div>
			</div>
		</div>
	);
};

export default PollEdit;
